import { Star, ExternalLink } from 'lucide-react';
import { GOOGLE_REVIEWS } from '@/lib/googleReviewsData';
import JsonLd from '@/components/smartpos/JsonLd';
import { useLanguage } from '@/lib/LanguageContext';

function Stars({ value, size = 14 }) {
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((n) =>
      <Star
        key={n}
        size={size}
        className={n <= Math.round(value) ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'} />
      )}
    </div>);
}

export default function GoogleReviews() {
  const { t, lang } = useLanguage();
  const { rating, user_ratings_total, url, reviews } = GOOGLE_REVIEWS;

  // Las traducciones van guardadas junto a cada reseña, así que aparecen al
  // instante al cambiar de idioma, sin esperas y sin gastar nada. Si se añade
  // una reseña nueva sin traducciones, se muestra su texto original en español.
  const storedTexts = lang && lang !== 'ES' ? reviews.map((rv) => rv.translations?.[lang]) : null;

  const reviewLd = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: 'SmartPOS',
    aggregateRating: {
      '@type': 'AggregateRating',
      ratingValue: rating,
      reviewCount: user_ratings_total,
      bestRating: 5,
      worstRating: 1
    },
    review: reviews.map((rv) => ({
      '@type': 'Review',
      author: { '@type': 'Person', name: rv.author },
      reviewRating: { '@type': 'Rating', ratingValue: rv.rating, bestRating: 5, worstRating: 1 },
      reviewBody: rv.text
    }))
  };

  return (
    <section className="py-20 bg-white border-t border-gray-100">
      <JsonLd data={reviewLd} />
      <div className="max-w-6xl mx-auto px-6">
        <div className="text-center mb-12">
          <span className="inline-block text-xs font-black tracking-widest uppercase text-blue-500 mb-4 px-3 py-1 bg-blue-50 rounded-full">
            {t('reviews.eyebrow')}
          </span>
          <h2 className="font-heading font-light text-4xl lg:text-5xl text-gray-900 leading-tight mb-4 tracking-tight">
            {t('reviews.title1')} <span className="text-brand-blue">{t('reviews.titleHighlight')}</span>
          </h2>
          <p className="text-gray-500 text-sm font-light max-w-xl mx-auto">
            {t('reviews.desc')}
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {reviews.map((rv, i) =>
          <div key={i} className="p-6 rounded-2xl bg-gray-50 border border-gray-100 flex flex-col">
              <div className="flex items-center gap-3 mb-3">
                {rv.profile_photo ?
              <img src={rv.profile_photo} alt={rv.author} loading="lazy" decoding="async" className="w-10 h-10 rounded-full object-cover" /> :

              <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-bold text-sm">
                    {rv.author?.[0] || '?'}
                  </div>
              }
                <div>
                  <p className="font-bold text-sm text-gray-900">{rv.author}</p>
                  <p className="text-gray-400 text-xs">{rv.relative_time || ''}</p>
                </div>
              </div>
              <Stars value={rv.rating} />
              <p className="text-gray-600 text-sm font-light leading-relaxed mt-3 line-clamp-5">
                {storedTexts?.[i] || rv.text}
              </p>
            </div>
          )}
        </div>

        {url &&
        <div className="text-center mt-10">
            <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 text-brand-blue font-bold text-sm hover:gap-3 transition-all">
            
              {t('reviews.viewAll')} <ExternalLink size={15} />
            </a>
          </div>
        }

      </div>
    </section>);

}